/**
 * Determines the network error type after a failed request.
 *
 * The login API lives on a different origin than the app's static assets
 * (e.g. `api.libertify.com` vs `app.libertify.com`). A failure on the API
 * origin doesn't necessarily mean the user has no internet — it can also
 * be a brief API outage, a CORS issue, an SSL problem, an ad-blocker
 * blocking the API subdomain, or a corporate firewall. Showing
 * "you're behind a VPN" to all of those users is wrong, so we discriminate
 * three states:
 *
 *   - "offline":   navigator says we're offline, or neither origin responds.
 *   - "vpn":       the app's own origin is reachable but the API is not —
 *                  consistent with a VPN/firewall/extension blocking the API.
 *   - "api_error": the API origin responded (any status) — the failure was
 *                  not a network issue (CORS, server-side, etc.). Don't
 *                  blame the user's network.
 *
 * Probes use `mode: "no-cors"` so an opaque response counts as "host
 * reachable" regardless of CORS headers or status code. Each probe has a
 * 3s timeout to avoid blocking the UX on a slow connection.
 */

export type NetworkErrorType = "vpn" | "offline" | "api_error";

const PROBE_TIMEOUT_MS = 3000;

const probe = async (url: string): Promise<boolean> => {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS);
  try {
    await fetch(url, {
      method: "HEAD",
      mode: "no-cors",
      cache: "no-store",
      credentials: "omit",
      signal: controller.signal,
    });
    return true;
  } catch {
    return false;
  } finally {
    clearTimeout(timeout);
  }
};

export const detectNetworkError = async (
  apiUrl?: string
): Promise<NetworkErrorType> => {
  if (typeof navigator !== "undefined" && navigator.onLine === false) {
    return "offline";
  }

  if (apiUrl) {
    const apiReachable = await probe(apiUrl);
    if (apiReachable) {
      return "api_error";
    }
  }

  const appReachable = await probe("/favicon.ico");
  return appReachable ? "vpn" : "offline";
};

/**
 * True when the error is an HTTP error carrying a 404 status code. Used to
 * treat "not found" as an empty/absent resource rather than a hard error.
 */
export const is404 = (error: unknown): boolean =>
  (error as { statusCode?: number } | undefined)?.statusCode === 404;

/**
 * True when an axios error indicates the request never reached a response
 * stage — i.e. a network-level failure rather than an HTTP error.
 */
export const isNetworkError = (error: unknown): boolean => {
  if (!error || typeof error !== "object") return false;
  const e = error as {
    message?: string;
    code?: string;
    response?: unknown;
    request?: unknown;
  };
  if (e.message === "Network Error") return true;
  if (e.code === "ERR_NETWORK") return true;
  if (e.code === "ECONNABORTED") return true;
  if (e.code === "ERR_INTERNET_DISCONNECTED") return true;
  if (e.request && !e.response) return true;
  return false;
};
